import clsx from 'clsx'
import React, { ReactNode, useMemo, useEffect, useCallback, useState } from 'react'

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'

import { Divider } from './Divider'
import { Icon } from './Icon/Icon'

interface SideModalProps {
  open: boolean
  children: ReactNode
  className?: string
  onModalClose?: () => void
}

interface SlotMap {
  header?: ReactNode
  body?: ReactNode
  actions?: ReactNode
  default?: ReactNode[]
}

export const SideModal = ({ open, children, className, onModalClose }: SideModalProps) => {
  // Keep the modal mounted while the exit animation plays, and drive the
  // slide/fade with a separate "visible" flag so the enter transition runs.
  const [isMounted, setIsMounted] = useState(open)
  const [isVisible, setIsVisible] = useState(false)

  const handleModalClose = useCallback(() => {
    onModalClose?.()
  }, [onModalClose])

  useEffect(() => {
    if (open) {
      setIsMounted(true)
      // Two frames: the first lets the freshly-mounted panel paint at its
      // off-screen position, the second flips it on so the slide-in animates.
      let innerRaf = 0
      const outerRaf = requestAnimationFrame(() => {
        innerRaf = requestAnimationFrame(() => setIsVisible(true))
      })
      return () => {
        cancelAnimationFrame(outerRaf)
        cancelAnimationFrame(innerRaf)
      }
    }
    setIsVisible(false)
  }, [open])

  // Unmount only after the closing (slide-out) transition finishes.
  const handleTransitionEnd = () => {
    if (!open) setIsMounted(false)
  }

  // ✅ Only handle side effects (body scroll + ESC key), never call open/close callbacks
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open]) // ← no callbacks in deps

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleModalClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [handleModalClose])

  const slots: SlotMap = useMemo(() => {
    const map: SlotMap = { default: [] }
    React.Children.forEach(children, child => {
      if (!React.isValidElement(child)) return
      const slotName = (child.props as { slot?: string })?.slot
      if (slotName === 'header') map.header = child
      else if (slotName === 'body') map.body = child
      else if (slotName === 'actions') map.actions = child
      else map.default?.push(child)
    })
    return map
  }, [children])

  if (!isMounted) return null

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end">
      <div
        className={clsx(
          'absolute inset-0 bg-black/40 transition-opacity duration-300 ease-in-out',
          isVisible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={handleModalClose}
      />
      <div
        role="dialog"
        onTransitionEnd={handleTransitionEnd}
        className={clsx(
          'relative w-[480px] max-w-full h-full bg-white shadow-xl flex flex-col transition-transform duration-300 ease-in-out',
          isVisible ? 'translate-x-0' : 'translate-x-full',
          className,
        )}
      >
        <button
          className="absolute right-[20px] top-[20px] border-none bg-transparent cursor-pointer p-0"
          onClick={handleModalClose}
        >
          <Icon icon={CloseIcon} aria-label="Clear icon" color="#8b919a" />
        </button>
        {slots.header && <div className="px-6 py-4 font-semibold text-lg">{slots.header}</div>}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col">{slots.body || slots.default}</div>
        <Divider className="mt-[16px]" />
        {slots.actions && <div className="px-6 py-4 flex justify-end gap-3">{slots.actions}</div>}
      </div>
    </div>
  )
}
